import { useMemo, useState } from 'react';

import { Box, Breadcrumbs, Link, styled, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useContainer } from 'unstated-next';

import AdvancedOptions from './FormComponents/AdvancedOptions';
import LTI13Settings from './FormComponents/LTI13Settings';
import LTI13Urls from './FormComponents/LTI13Urls';
import NameAndLMS from './FormComponents/NameInputAndLMSSelect';
import LTITypeSelect from './FormComponents/VersionSelect';
import IntegrationsHeader from './IntegrationsHeader';
import { LmsTypeCapitalized } from '../../../../components/classroom/types';
import { StyledLoadingButton } from '../../../../components/forms/LoadingButton';
import type { LTICredential } from '../../../../types/apiTypes';
import AdminIntegrationsContainer, { LTIVersion } from '../../AdminIntegrationsContainer';

const StyledFormDiv = styled(Box)`
    display: flex;
    flex-direction: column;
    margin-top: 12px;
`;

const StyledSaveButtonDiv = styled(Box)`
    display: flex;
    margin-top: 24px;
`;

const StyledBreadcrumbs = styled(Breadcrumbs)`
    margin-top: 14px;
`;

const IntegrationCreate = () => {
    const { t } = useTranslation('admin_settings');
    const { createIntegration, updateIntegration, deleteIntegration, fetchIntegrationList, setSelectedIntegration } =
        useContainer(AdminIntegrationsContainer);
    const [onStepTwo, setOnStepTwo] = useState<boolean>(false);
    const [saving, setSaving] = useState<boolean>(false);
    const [canceling, setCanceling] = useState<boolean>(false);
    const { path } = useRouteMatch();
    const history = useHistory();

    const [formState, setFormState] = useState<
        Omit<
            LTICredential,
            | 'zoom_login_id'
            | 'client_id'
            | 'shared_secret'
            | 'school_uuid'
            | 'active'
            | 'rsa_public_key'
            | 'created_at'
            | 'updated_at'
        >
    >({
        lti_creds_uuid: '',
        title: '',
        type: '',
        email_attribute_name: '',
        course_id_attribute_name: '',
        course_id_prefix: '',
        person_id_attribute_name: '',
        person_id_prefix: '',
        lti_version: LTIVersion.V1_1 as string,
        learn_app_key: '',
        learn_app_secret: '',
        consumer_client_id: '',
        consumer_authorization_url: '',
        consumer_access_token_url: '',
        consumer_authorization_config_url: '',
        debug_mode: false as boolean,
        grant_instructor_system_role: true as boolean
    });

    const handleChange = (event) => {
        const { name, value, checked } = event.target;
        if (name === 'type') {
            if (value === LmsTypeCapitalized.Docebo) {
                setFormState((prevState) => ({
                    ...prevState,
                    type: value,
                    lti_version: LTIVersion.V1_3
                }));
                return;
            }
            if (value === LmsTypeCapitalized.CSOD) {
                setFormState((prevState) => ({
                    ...prevState,
                    type: value,
                    lti_version: LTIVersion.NONE
                }));
                return;
            }
        }
        if (['debug_mode', 'grant_instructor_system_role'].includes(name)) {
            setFormState((prevState) => ({
                ...prevState,
                [name]: checked
            }));
            return;
        }
        setFormState((prevState) => ({
            ...prevState,
            [name]: value
        }));
    };

    const {
        lti_creds_uuid: ltiCredsUuid,
        title,
        type: integrationType,
        email_attribute_name: emailAttributeName,
        course_id_attribute_name: courseIdAttributeName,
        course_id_prefix: courseIdPrefix,
        person_id_attribute_name: personIdAttributeName,
        person_id_prefix: personIdPrefix,
        lti_version: ltiVersion,
        learn_app_key: consumerAppKey,
        learn_app_secret: consumerSecret,
        consumer_client_id: consumerClientId,
        consumer_authorization_url: consumerAuthorizationUrl,
        consumer_access_token_url: consumerAccessTokenUrl,
        consumer_authorization_config_url: consumerAuthorizationConfigUrl,
        debug_mode: debugMode,
        grant_instructor_system_role: grantInstructorSystemRole,
        type: selectedLMS
    } = formState;

    const isLTI13 = ltiVersion === '1.3';

    const checkForEmptyValues = () => {
        if (!title || !integrationType) {
            return true;
        }

        return (
            isLTI13 &&
            onStepTwo &&
            (!consumerClientId ||
                !consumerAuthorizationUrl ||
                !consumerAccessTokenUrl ||
                !consumerAuthorizationConfigUrl)
        );
    };
    const hasLTI = useMemo(() => integrationType !== LmsTypeCapitalized.CSOD, [integrationType]);
    
    const handleSave = async (event) => {
        event.preventDefault();
        setSaving(true);

        if (isLTI13) {
            if (!onStepTwo) {
                const createdRecord = await createIntegration(formState);
                if (!createdRecord) {
                    setSaving(false);
                    return;
                }

                setFormState(createdRecord);
                setSelectedIntegration(createdRecord);
                setSaving(false);
                setOnStepTwo(true);
                return;
            }

            await updateIntegration(formState);
            await fetchIntegrationList();
            setSaving(false);
            history.push(`${path.replace('/create', '')}`);
            return;
        }

        await createIntegration(formState);
        await fetchIntegrationList();
        setSaving(false);
        history.push(`${path.replace('/create', '')}`);
    };

    const handleCancel = async (event) => {
        event.preventDefault();
        setCanceling(true);
        if (isLTI13 && onStepTwo) {
            await deleteIntegration();
            setCanceling(false);
            history.push(`${path.replace('/create', '')}`);
        }
        setCanceling(false);
        history.push(`${path.replace('/create', '')}`);
    };

    return (
        <>
            <IntegrationsHeader />

            <StyledBreadcrumbs>
                <Link variant="body1" underline="hover" color="primary" onClick={() => history.goBack()}>
                    {t('lms-configurations')}
                </Link>
                <Typography variant="body1" color="text.primary">
                    {t('create')}
                </Typography>
            </StyledBreadcrumbs>

            <StyledFormDiv>
                <NameAndLMS title={title} type={integrationType} handleChange={handleChange} />
                {hasLTI && (<LTITypeSelect ltiVersion={ltiVersion} type={integrationType} handleChange={handleChange} />)}

                {isLTI13 && onStepTwo && (
                    <>
                        <LTI13Urls ltiCredsUuid={ltiCredsUuid} selectedLMS={selectedLMS} />
                        <LTI13Settings
                            integrationType={integrationType}
                            consumerAppKey={consumerAppKey}
                            consumerSecret={consumerSecret}
                            selectedLMS={selectedLMS}
                            consumerClientId={consumerClientId}
                            consumerAuthorizationUrl={consumerAuthorizationUrl}
                            consumerAccessTokenUrl={consumerAccessTokenUrl}
                            consumerAuthorizationConfigUrl={consumerAuthorizationConfigUrl}
                            handleChange={handleChange}
                        />
                    </>
                )}

                <AdvancedOptions
                    emailAttributeName={emailAttributeName}
                    courseIdAttributeName={courseIdAttributeName}
                    courseIdPrefix={courseIdPrefix}
                    personIdAttributeName={personIdAttributeName}
                    personIdPrefix={personIdPrefix}
                    debugMode={debugMode}
                    hasLTI={hasLTI}
                    grantInstructorSystemRole={grantInstructorSystemRole}
                    handleChange={handleChange}
                />

                <StyledSaveButtonDiv>
                    <StyledLoadingButton
                        onClick={handleCancel}
                        variant="outlined"
                        loading={canceling}
                        disabled={saving}
                    >
                        {t('cancel')}
                    </StyledLoadingButton>

                    <StyledLoadingButton
                        sx={{ marginLeft: '10px' }}
                        onClick={handleSave}
                        disabled={checkForEmptyValues() || canceling}
                        variant="contained"
                        loading={saving}
                    >
                        {isLTI13 && !onStepTwo ? t('next') : t('save')}
                    </StyledLoadingButton>
                </StyledSaveButtonDiv>
            </StyledFormDiv>
        </>
    );
};

export default IntegrationCreate;
